import { api, API_ENDPOINTS } from '../lib/api';
import { BackendLocation, SearchLocationResult } from '../types/location';

/**
 * Nearby locations query parameters
 */
export interface NearbyLocationsParams {
  latitude: number;
  longitude: number;
  radius?: number; // in km, default 10
  limit?: number; // max results, default 50
}

export interface SearchLocationsParams {
  q: string;
  latitude?: number;
  longitude?: number;
  limit?: number;
  vehicleId?: string;
}

/**
 * Location Service
 * Handles location-related API requests
 */
export const locationService = {
  /**
   * Get nearby charging locations based on user coordinates
   * @param params - Location query parameters
   * @returns Array of locations with distance calculated
   */
  getNearbyLocations: async (params: NearbyLocationsParams): Promise<BackendLocation[]> => {
    const queryParams = {
      latitude: params.latitude,
      longitude: params.longitude,
      radius: params.radius || 10,
      limit: params.limit || 50,
    };

    const response = await api.get<BackendLocation[]>(API_ENDPOINTS.LOCATIONS.NEARBY, {
      params: queryParams,
    });

    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Get location by ID
   * @param locationId - Location ID
   * @returns Location details
   */
  getLocationById: async (locationId: string): Promise<BackendLocation> => {
    const response = await api.get<BackendLocation>(API_ENDPOINTS.LOCATIONS.BY_ID(locationId));
    return response.data;
  },

  /**
   * Get all locations
   * @returns Array of all locations
   */
  getAllLocations: async (): Promise<BackendLocation[]> => {
    const response = await api.get<BackendLocation[]>(API_ENDPOINTS.LOCATIONS.BASE);
    return Array.isArray(response.data) ? response.data : [];
  },

  searchLocations: async (params: SearchLocationsParams): Promise<SearchLocationResult[]> => {
    const response = await api.get<SearchLocationResult[]>(API_ENDPOINTS.LOCATIONS.SEARCH, {
      params,
    });
    return Array.isArray(response.data) ? response.data : [];
  },
};
